import { useMemo } from 'react';
import { TASK_DESCRIPTION_MAX_LENGTH, useTaskOne } from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { useNotification } from '@sib/notifications';
import {
  Button,
  Form,
  FormCheckbox,
  FormSelect,
  FormTextarea,
  FormTextField,
  FormCombobox,
  FormDatePicker,
  FormTaskTagField,
  FormToggleGroup,
  DeactivatedUserOption,
  ComboboxAvatarItem,
} from '@sib/ui';
import { usePriorityOptions } from '@sib/ui/hooks';
import {
  CALENDAR_END_MONTH,
  CALENDAR_START_MONTH,
  FormMode,
  getInitials,
  shouldLockContextField,
  useAssigneeAutocompleteProps,
  useTaskCategoryOptions,
  useTaskStatusOptionsWithIcons,
} from '@sib/utils';
import { TaskField } from '@sib/utils/tasks';
import { TaskFormSkeleton } from './TaskFormSkeleton';
import {
  TaskFormEntityContext,
  useTaskFieldsOptions,
  useTaskForm,
} from './TaskForm.utils';

export interface TaskFormProps {
  taskId?: string;
  type: FormMode;
  onClose?: () => void;
  onCancel?: () => void;
  cancelLabel?: string;
  taskContext?: TaskFormEntityContext;
  showCompletedField?: boolean;
  /** Creates the task as a next step of the given task. */
  nextStepParentTaskId?: string | null;
}

export const TaskForm = ({
  taskId,
  type,
  onClose,
  onCancel,
  cancelLabel,
  taskContext,
  showCompletedField,
  nextStepParentTaskId,
}: TaskFormProps) => {
  const { t } = useTranslation();
  const { open } = useNotification();
  const priorityOptions = usePriorityOptions();
  const taskStatusOptions = useTaskStatusOptionsWithIcons();

  const {
    formProps,
    isEdit,
    isLoadingTask,
    currentTaskId,
    onSubmit,
    onCreateNextStep,
    onCancel: onCancelHandler,
    isSubmitting,
  } = useTaskForm({
    taskId,
    type,
    onClose,
    onCancel,
    taskContext,
    nextStepParentTaskId,
  });
  const { watch } = formProps;
  const isNextStep = watch(TaskField.IsNextStep);
  const { data: task } = useTaskOne(currentTaskId || '');
  const { isValid } = formProps.formState;

  const shouldDisableOrganization = shouldLockContextField(
    taskContext?.organizationId,
    watch(TaskField.Organization)
  );
  const shouldDisableVendor = shouldLockContextField(
    taskContext?.vendorId,
    watch(TaskField.Vendor)
  );
  const shouldDisableTicket = shouldLockContextField(
    taskContext?.ticketId,
    watch(TaskField.Ticket)
  );

  const {
    organizationsAutocompleteProps,
    vendorsAutocompleteProps,
    ticketsAutocompleteProps,
  } = useTaskFieldsOptions({
    taskId: currentTaskId,
    defaultOrganizationId: taskContext?.organizationId,
    defaultVendorId: taskContext?.vendorId,
    defaultTicketId: taskContext?.ticketId,
  });

  const assigneeAutocompleteProps = useAssigneeAutocompleteProps(
    useMemo(
      () => ({
        additionalFields: task?.assigneeUser
          ? [
              {
                label: task?.assigneeUser?.deletedAt ? (
                  <DeactivatedUserOption label={task?.assigneeUser?.name} />
                ) : (
                  task?.assigneeUser.name
                ),
                value: task?.assigneeUser.id,
                initial: getInitials(task?.assigneeUser.name),
                avatarUrl: task?.assigneeUser?.avatarUrl,
              },
            ]
          : [],
      }),
      [task?.assigneeUser]
    )
  );

  const createdByUser = task?.createdByUser;
  const authorOptions = useMemo(
    () =>
      createdByUser
        ? [
            {
              label: createdByUser.deletedAt ? (
                <DeactivatedUserOption label={createdByUser.name} />
              ) : (
                createdByUser.name
              ),
              value: createdByUser.id,
              initial: getInitials(createdByUser.name),
              avatarUrl: createdByUser.avatarUrl,
            },
          ]
        : [],
    [createdByUser]
  );

  const categoriesOptions = useTaskCategoryOptions();
  const estimatedTime = watch(TaskField.EstimatedTime);
  const showEstimatedTimeSuffix =
    typeof estimatedTime === 'number' && estimatedTime > 0;

  if (isLoadingTask && isEdit) return <TaskFormSkeleton />;

  return (
    <Form
      {...formProps}
      onSubmit={onSubmit}
      className="flex h-full min-h-0 w-full flex-1 flex-col gap-4"
    >
      <div className="flex min-h-0 w-full flex-1 flex-col gap-5 overflow-y-auto">
        <div className="flex w-full flex-col gap-5">
          <FormTextField
            name={TaskField.Title}
            label={t('components.taskForm.fields.title.label')}
            placeholder={t('components.taskForm.fields.title.placeholder')}
            required
            readOnly={isSubmitting}
          />
          <FormTextarea
            rows={5}
            className="resize-none"
            name={TaskField.Description}
            label={t('components.taskForm.fields.description.label')}
            placeholder={t(
              'components.taskForm.fields.description.placeholder'
            )}
            maxLength={TASK_DESCRIPTION_MAX_LENGTH}
            readOnly={isSubmitting}
          />
        </div>

        <div className="grid w-full grid-cols-1 gap-5 sm:grid-cols-2">
          <FormToggleGroup
            type="single"
            label={t('components.actionItemForm.fields.priority')}
            className="h-12 w-full"
            name={TaskField.PriorityLevel}
            options={priorityOptions}
            groupItemsProps={{
              variant: 'chip',
            }}
            disabled={isSubmitting}
          />
          <FormSelect
            label={t('components.taskForm.fields.category.label')}
            name={TaskField.Category}
            options={categoriesOptions}
            placeholder={t('notSelected')}
            disabled={isSubmitting}
          />
          <FormSelect
            label={t('components.tasksTable.columns.status')}
            name={TaskField.Status}
            options={taskStatusOptions}
            placeholder={t('notSelected')}
            disabled={isSubmitting}
          />
          <FormTaskTagField
            initialTags={task?.tags ?? []}
            callbacks={{
              onTagCreateSuccess: () => {
                open({
                  type: 'success',
                  message: t(
                    'successMessages.client.TAG_CREATION_SUCCESS.message'
                  ),
                  description: t(
                    'successMessages.client.TAG_CREATION_SUCCESS.description'
                  ),
                });
              },
              onTagDeleteSuccess: () => {
                open({
                  type: 'success',
                  message: t(
                    'successMessages.client.TAG_DELETION_SUCCESS.message'
                  ),
                  description: t(
                    'successMessages.client.TAG_DELETION_SUCCESS.description'
                  ),
                });
              },
              onTagCreateError: () => {
                open({
                  type: 'error',
                  message: t('errors.client.TAG_ALREADY_EXISTS.message'),
                  description: t(
                    'errors.client.TAG_ALREADY_EXISTS.description'
                  ),
                });
              },
              onTagDeleteError: () => {
                open({
                  type: 'error',
                  message: t('errors.client.TAG_DELETION_FAILED.message'),
                  description: t(
                    'errors.client.TAG_DELETION_FAILED.description'
                  ),
                });
              },
            }}
          />
          <div className="flex flex-col gap-2">
            <FormTextField
              name={TaskField.ReferenceName}
              label={t('components.taskForm.fields.reference.label')}
              placeholder={t(
                'components.taskForm.fields.referenceName.placeholder'
              )}
              disabled={isSubmitting}
            />
            <FormTextField
              name={TaskField.ReferenceLink}
              placeholder={t(
                'components.taskForm.fields.referenceLink.placeholder'
              )}
              disabled={isSubmitting}
            />
          </div>

          <FormCombobox
            name={TaskField.Organization}
            label={t('components.taskForm.fields.organization.label')}
            {...organizationsAutocompleteProps}
            allowNullValue
            readOnly={shouldDisableOrganization}
            disabled={isSubmitting}
          />
          <FormCombobox
            name={TaskField.Vendor}
            label={t('components.taskForm.fields.vendor.label')}
            {...vendorsAutocompleteProps}
            allowNullValue
            readOnly={shouldDisableVendor}
            disabled={isSubmitting}
          />
          <FormCombobox
            name={TaskField.Ticket}
            label={t('components.taskForm.fields.ticket.label')}
            placeholder={t('notSelected')}
            {...ticketsAutocompleteProps}
            searchPlaceholder={t(
              'components.taskForm.fields.ticket.searchPlaceholder'
            )}
            allowNullValue
            readOnly={shouldDisableTicket}
            disabled={isSubmitting}
          />
          <FormDatePicker
            name={TaskField.Deadline}
            label={t('components.taskForm.fields.deadline.label')}
            required
            confirmLabel=""
            disabled={isSubmitting}
            captionLayout="dropdown"
            startMonth={CALENDAR_START_MONTH}
            endMonth={CALENDAR_END_MONTH}
          />
          <FormCombobox
            name={TaskField.Assignee}
            label={t('components.taskForm.fields.assignedTo.label')}
            placeholder={t('notSelected')}
            {...assigneeAutocompleteProps}
          />
          {isEdit && createdByUser && (
            <FormCombobox
              name={TaskField.CreatedBy}
              label={t('components.taskForm.fields.author.label')}
              options={authorOptions}
              OptionComponent={ComboboxAvatarItem}
              readOnly
              placeholder={t('notSelected')}
            />
          )}
          <FormTextField
            name={TaskField.EstimatedTime}
            label={t('components.taskForm.fields.estimatedTime.label')}
            placeholder={t(
              'components.taskForm.fields.estimatedTime.placeholder'
            )}
            type="number"
            InputProps={{
              className: '[&::-webkit-inner-spin-button]:appearance-none ',
            }}
            {...(showEstimatedTimeSuffix && {
              endAdornment: <span>{t('time.min')}</span>,
              endAdoramentProps: { className: 'cursor-default' },
            })}
          />
        </div>
        {showCompletedField && (
          <FormCheckbox
            label={t('components.taskForm.fields.completed.label')}
            name={TaskField.IsCompleted}
            disabled={isSubmitting}
          />
        )}
      </div>
      <div className="flex shrink-0 justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={isSubmitting}
          onClick={onCancelHandler}
        >
          {cancelLabel ?? t('cancel')}
        </Button>
        {!isNextStep && (
          <Button
            type="button"
            variant="outline"
            loading={isSubmitting}
            onClick={onCreateNextStep}
          >
            {t('components.taskForm.buttons.createNextStep')}
          </Button>
        )}
        <Button
          type="submit"
          disabled={isSubmitting || !isValid}
          loading={isSubmitting}
        >
          {isEdit ? t('update') : t('create')}
        </Button>
      </div>
    </Form>
  );
};
